import React from 'react';
import NoAccess from '../components/NoAccess';
import { useRBAC } from './useRBAC';
import type { UserClaims } from './useRBAC';
import {
  SCREEN_ACCESS,
  ScreenKey,
  accessUnknownMessage,
  canViewScreen,
  noAccessMessage,
  screenAccessVerdict,
} from './screenAccess';

/**
 * THE GATE EVERY GATED ROUTE GOES THROUGH (INV-108).
 *
 * ⚠ IT LIVES HERE RATHER THAN IN `App.tsx` FOR A TESTING REASON THAT IS ALSO A DESIGN ONE.
 * Importing `App.tsx` pulls in every page and, through them, `config/firebase` and `config/faro`,
 * neither of which loads under jsdom — which is why the A8 wiring is pinned by a SOURCE parse
 * (`App.reportsAccess.test.ts`) rather than a render. A source parse cannot prove that a refused
 * screen never MOUNTS, and that property is the whole point: an unmounted screen fires no reads,
 * so there are no 403s for the offline layer to mishandle and no half-rendered page behind the
 * sentence. In its own module the wrapper renders under jsdom and `ScreenRoute.test.tsx` asserts
 * exactly that.
 *
 * ⚠ THERE IS NO REDIRECT. `PermissionRoute` used to `<Navigate to="/" />` on a refusal, which
 * (a) is indistinguishable from the redirect a failed permission READ used to cause, and (b) is
 * not what the product owner asked for: he asked to be told. Both refusal states — "you may not"
 * and "we could not check" — are one calm block, in place, on the URL the person typed.
 */
const ScreenGate: React.FC<{
  noun: string;
  allowed: (claims: UserClaims | null, hasPermission: (permission: string) => boolean) => boolean;
  children: React.ReactNode;
}> = ({ noun, allowed, children }) => {
  const { claims, loading, permissionsPending, permissionsUnavailable, hasPermission } = useRBAC();
  const verdict = screenAccessVerdict({
    loading,
    permissionsPending,
    permissionsUnavailable,
    claims,
    allowed: allowed(claims, hasPermission),
  });
  /**
   * ONCE OPEN, THIS MOUNT STAYS OPEN.
   *
   * The verdict is re-computed on every render, so a mid-session change could flip a screen that
   * is ALREADY OPEN to a refusal and unmount it — taking an unsaved SOAP note, a half-filled
   * invoice or a part-written order with it, and replacing them with a sentence. The causes are
   * all real and none of them is worth that: an owner editing permissions in another tab, a
   * definitive re-read failure after the 30 s TTL, a token refresh that drops the identity.
   *
   * Latching costs nothing in safety. The server refuses the write either way — this decides what
   * to RENDER, never what to serve (INV-104) — and the person is already looking at the data, so
   * hiding the page now protects nothing that has not already been seen. The revoke binds on the
   * next navigation or reload, which is where an access change belongs.
   *
   * ⚠ `deny → allow` STILL UPDATES: the latch only remembers that `allow` HAPPENED, so a grant
   * arriving while the sentence is on screen opens the page. And it is per MOUNT — a ref, not
   * state, so no re-render — which matters because React Router keeps a wrapper instance across
   * sibling routes: `useRef` here would survive a route change, so the ref is keyed by the screen
   * the gate was opened FOR and re-locks when that changes.
   */
  const openedFor = React.useRef<string | null>(null);
  if (verdict === 'allow') openedFor.current = noun;
  const latched = openedFor.current === noun;

  // Render NOTHING until the answer is in (INV-107 trap (a)) — never a verdict, and never the
  // screen's own empty state, which reads as "there is nothing here" rather than "we are asking".
  if (verdict === 'pending') return latched ? <>{children}</> : null;
  if (verdict === 'allow' || latched) return <>{children}</>;
  return <NoAccess message={verdict === 'unknown' ? accessUnknownMessage(noun) : noAccessMessage(noun)} />;
};

/** A route in `SCREEN_ACCESS`: the person needs every `view:` slug the server's list read needs. */
export const ScreenRoute: React.FC<{ screen: ScreenKey; children: React.ReactNode }> = ({
  screen,
  children,
}) => {
  const entry = SCREEN_ACCESS[screen];
  return (
    <ScreenGate noun={entry.noun} allowed={(_claims, hasPermission) => canViewScreen(entry, hasPermission)}>
      {children}
    </ScreenGate>
  );
};

/**
 * The same gate for a screen whose rule is NOT the table's — today only Reports, which asks
 * `auth/canOpenReports.ts` because `view:reports` is owner-locked server-side and the others are
 * not. Sharing the gate is what keeps the two screens' refusals one behaviour rather than two.
 */
export const CustomScreenRoute: React.FC<{
  noun: string;
  allowed: (claims: UserClaims | null, hasPermission: (permission: string) => boolean) => boolean;
  children: React.ReactNode;
}> = ScreenGate;
